'use client';
import React, { useState, useMemo } from 'react';
import GITable from './GITable';
import GIWizard from './GIWizard';
import GIDetailPanel from './GIDetailPanel';
import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';

export type GIStatus = 'DRAFT' | 'PENDING_APPROVAL' | 'APPROVED' | 'ISSUED';

export interface GILineItem {
  id: string;
  itemCode: string;
  itemName: string;
  requestedQty: number;
  issuedQty: number;
  uom: string;
  unitCost: number;
  totalCost: number;
  availableQty: number;
}

export interface GI {
  id: string;
  giNumber: string;
  requestedBy: string;
  site: string;
  bomProject?: string;
  bomCode?: string;
  purpose: string;
  status: GIStatus;
  createdDate: string;
  approvedBy?: string;
  approvedDate?: string;
  issuedDate?: string;
  recipient?: string;
  signatureCaptured?: boolean;
  lineItems: GILineItem[];
  totalValue: number;
  gatePasPrinted?: boolean;
}

export const MOCK_GIS: GI[] = [
  {
    id: 'gi-001',
    giNumber: 'GI-2026-0319',
    requestedBy: 'Rahul Kumar',
    site: 'Site Beta',
    bomProject: 'Steel Structure — Tower 2',
    bomCode: 'PROJ-2026-014',
    purpose: 'Structural steel works — Tower 2 column erection',
    status: 'PENDING_APPROVAL',
    createdDate: '28 Jul 2026',
    totalValue: 4200,
    lineItems: [
      { id: 'gli-gi-001-1', itemCode: 'STL-REBAR-12', itemName: 'TMT Steel Rebar 12mm', requestedQty: 6, issuedQty: 0, uom: 'MT', unitCost: 680, totalCost: 4080, availableQty: 8 },
      { id: 'gli-gi-001-2', itemCode: 'WELD-ROD-308', itemName: 'Welding Electrode SS 308', requestedQty: 8, issuedQty: 0, uom: 'KG', unitCost: 14.20, totalCost: 113.60, availableQty: 22 },
    ],
  },
  {
    id: 'gi-002',
    giNumber: 'GI-2026-0318',
    requestedBy: 'Meena Pillai',
    site: 'Site Gamma',
    purpose: 'PPE replenishment — monthly site allocation',
    status: 'PENDING_APPROVAL',
    createdDate: '28 Jul 2026',
    totalValue: 1890,
    lineItems: [
      { id: 'gli-gi-002-1', itemCode: 'PPE-GLOVE-L', itemName: 'Nitrile Gloves (Large)', requestedQty: 50, issuedQty: 0, uom: 'PR', unitCost: 0.95, totalCost: 47.50, availableQty: 24 },
      { id: 'gli-gi-002-2', itemCode: 'PPE-HELM-WHT', itemName: 'Safety Helmet White (ISI)', requestedQty: 10, issuedQty: 0, uom: 'EA', unitCost: 12.50, totalCost: 125, availableQty: 13 },
    ],
  },
  {
    id: 'gi-003',
    giNumber: 'GI-2026-0317',
    requestedBy: 'Kiran Bhat',
    site: 'Warehouse B',
    bomProject: 'HVAC Ductwork Phase 2',
    bomCode: 'PROJ-2026-013',
    purpose: 'HVAC ductwork installation — Phase 2 materials',
    status: 'ISSUED',
    createdDate: '27 Jul 2026',
    approvedBy: 'Arjun Mehta',
    approvedDate: '27 Jul 2026',
    issuedDate: '28 Jul 2026',
    recipient: 'Kiran Bhat',
    signatureCaptured: true,
    gatePasPrinted: true,
    totalValue: 8740,
    lineItems: [
      { id: 'gli-gi-003-1', itemCode: 'ELE-CBL-25', itemName: 'Electrical Cable 2.5mm²', requestedQty: 500, issuedQty: 500, uom: 'M', unitCost: 1.35, totalCost: 675, availableQty: 360 },
      { id: 'gli-gi-003-2', itemCode: 'TOOL-GRND-4IN', itemName: 'Angle Grinder 4" 850W', requestedQty: 3, issuedQty: 3, uom: 'EA', unitCost: 68, totalCost: 204, availableQty: 5 },
      { id: 'gli-gi-003-3', itemCode: 'LUB-HYD-46', itemName: 'Hydraulic Oil ISO 46', requestedQty: 0, issuedQty: 0, uom: 'L', unitCost: 4.80, totalCost: 0, availableQty: 4 },
    ],
  },
  {
    id: 'gi-004',
    giNumber: 'GI-2026-0316',
    requestedBy: 'Sunita Rao',
    site: 'Site Beta',
    bomProject: 'Electrical Conduit Install',
    bomCode: 'PROJ-2026-012',
    purpose: 'Electrical conduit installation — Phase 3',
    status: 'APPROVED',
    createdDate: '26 Jul 2026',
    approvedBy: 'Arjun Mehta',
    approvedDate: '27 Jul 2026',
    totalValue: 3240,
    lineItems: [
      { id: 'gli-gi-004-1', itemCode: 'ELE-CBL-25', itemName: 'Electrical Cable 2.5mm²', requestedQty: 800, issuedQty: 0, uom: 'M', unitCost: 1.35, totalCost: 1080, availableQty: 360 },
      { id: 'gli-gi-004-2', itemCode: 'SEAL-ORING-32', itemName: 'O-Ring NBR 32mm ID', requestedQty: 40, issuedQty: 0, uom: 'EA', unitCost: 0.45, totalCost: 18, availableQty: 148 },
    ],
  },
  {
    id: 'gi-005',
    giNumber: 'GI-2026-0315',
    requestedBy: 'Anita Desai',
    site: 'Site Alpha',
    bomProject: 'Foundation Works — Block C',
    bomCode: 'PROJ-2026-011',
    purpose: 'Foundation concrete works — Block C footings',
    status: 'ISSUED',
    createdDate: '25 Jul 2026',
    approvedBy: 'Arjun Mehta',
    approvedDate: '25 Jul 2026',
    issuedDate: '26 Jul 2026',
    recipient: 'Anita Desai',
    signatureCaptured: true,
    gatePasPrinted: true,
    totalValue: 14400,
    lineItems: [
      { id: 'gli-gi-005-1', itemCode: 'CHEM-CEM-OPC', itemName: 'OPC Cement 53 Grade', requestedQty: 200, issuedQty: 200, uom: 'BAG', unitCost: 7.20, totalCost: 1440, availableQty: 420 },
      { id: 'gli-gi-005-2', itemCode: 'STL-REBAR-12', itemName: 'TMT Steel Rebar 12mm', requestedQty: 18, issuedQty: 18, uom: 'MT', unitCost: 680, totalCost: 12240, availableQty: 8 },
    ],
  },
  {
    id: 'gi-006',
    giNumber: 'GI-2026-0314',
    requestedBy: 'Rahul Kumar',
    site: 'Warehouse B',
    purpose: 'Maintenance consumables — monthly issue',
    status: 'DRAFT',
    createdDate: '24 Jul 2026',
    totalValue: 2180,
    lineItems: [
      { id: 'gli-gi-006-1', itemCode: 'LUB-HYD-46', itemName: 'Hydraulic Oil ISO 46', requestedQty: 100, issuedQty: 0, uom: 'L', unitCost: 4.80, totalCost: 480, availableQty: 4 },
      { id: 'gli-gi-006-2', itemCode: 'MECH-BRNG-6205', itemName: 'Deep Groove Ball Bearing 6205', requestedQty: 10, issuedQty: 0, uom: 'EA', unitCost: 18.40, totalCost: 184, availableQty: 2 },
    ],
  },
  {
    id: 'gi-007',
    giNumber: 'GI-2026-0313',
    requestedBy: 'Priya Sharma',
    site: 'Site Alpha',
    purpose: 'QHSE PPE emergency replenishment',
    status: 'ISSUED',
    createdDate: '22 Jul 2026',
    approvedBy: 'Arjun Mehta',
    approvedDate: '22 Jul 2026',
    issuedDate: '22 Jul 2026',
    recipient: 'Priya Sharma',
    signatureCaptured: true,
    totalValue: 710,
    lineItems: [
      { id: 'gli-gi-007-1', itemCode: 'SAF-FEXT-CO2', itemName: 'Fire Extinguisher CO₂ 5kg', requestedQty: 4, issuedQty: 4, uom: 'EA', unitCost: 89.50, totalCost: 358, availableQty: 12 },
      { id: 'gli-gi-007-2', itemCode: 'PPE-HELM-WHT', itemName: 'Safety Helmet White (ISI)', requestedQty: 10, issuedQty: 10, uom: 'EA', unitCost: 12.50, totalCost: 125, availableQty: 13 },
    ],
  },
  {
    id: 'gi-008',
    giNumber: 'GI-2026-0312',
    requestedBy: 'Kiran Bhat',
    site: 'Warehouse B',
    bomProject: 'Plumbing & Drainage Block D',
    bomCode: 'PROJ-2026-015',
    purpose: 'Plumbing materials — over-consumption justification approved by PM',
    status: 'ISSUED',
    createdDate: '20 Jul 2026',
    approvedBy: 'Arjun Mehta',
    approvedDate: '20 Jul 2026',
    issuedDate: '21 Jul 2026',
    recipient: 'Kiran Bhat',
    signatureCaptured: true,
    totalValue: 6840,
    lineItems: [
      { id: 'gli-gi-008-1', itemCode: 'SEAL-ORING-32', itemName: 'O-Ring NBR 32mm ID', requestedQty: 200, issuedQty: 200, uom: 'EA', unitCost: 0.45, totalCost: 90, availableQty: 148 },
      { id: 'gli-gi-008-2', itemCode: 'WELD-ROD-308', itemName: 'Welding Electrode SS 308', requestedQty: 120, issuedQty: 120, uom: 'KG', unitCost: 14.20, totalCost: 1704, availableQty: 22 },
    ],
  },
];

const STATUS_TABS = ['ALL', 'DRAFT', 'PENDING_APPROVAL', 'APPROVED', 'ISSUED'] as const;

export default function GIContent() {
  const [statusTab, setStatusTab] = useState<typeof STATUS_TABS[number]>('ALL');
  const [search, setSearch] = useState('');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [detailGI, setDetailGI] = useState<GI | null>(null);

  const filtered = useMemo(() => {
    return MOCK_GIS.filter((g) => {
      const matchTab = statusTab === 'ALL' || g.status === statusTab;
      const matchSearch = search === '' ||
        g.giNumber.toLowerCase().includes(search.toLowerCase()) ||
        g.requestedBy.toLowerCase().includes(search.toLowerCase()) ||
        (g.bomCode ?? '').toLowerCase().includes(search.toLowerCase()) ||
        g.purpose.toLowerCase().includes(search.toLowerCase());
      return matchTab && matchSearch;
    });
  }, [statusTab, search]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { ALL: MOCK_GIS.length };
    STATUS_TABS.slice(1).forEach((s) => { c[s] = MOCK_GIS.filter((g) => g.status === s).length; });
    return c;
  }, []);

  const pendingPhysical = MOCK_GIS.filter((g) => g.status === 'APPROVED').length;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-600 text-foreground">Goods Issuance</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Request → Approve → Physical Issue with digital signature and gate pass
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => toast.success('Issuance register exported.')}
            className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg text-sm font-500 hover:bg-muted transition-colors bg-card"
          >
            <Icon name="ArrowDownTrayIcon" size={15} />
            Export Register
          </button>
          <button
            onClick={() => setWizardOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-accent text-accent-foreground rounded-lg text-sm font-600 hover:bg-amber-500 transition-all active:scale-95 shadow-card"
          >
            <Icon name="PlusIcon" size={16} />
            New GI Request
          </button>
        </div>
      </div>

      {/* Workflow strip */}
      <div className="flex items-center gap-4 px-4 py-3 bg-teal/5 border border-teal/20 rounded-xl">
        {[
          { step: 1, label: 'REQUEST', desc: 'Draft entry', icon: 'DocumentPlusIcon' },
          { step: 2, label: 'APPROVE', desc: 'Manager approval', icon: 'CheckCircleIcon' },
          { step: 3, label: 'ISSUE', desc: 'Physical + signature', icon: 'TruckIcon' },
        ].map((s, i) => (
          <React.Fragment key={`gi-wf-${s.step}`}>
            <div className="flex items-center gap-2 text-muted-foreground">
              <div className="w-7 h-7 rounded-full bg-teal/10 text-teal flex items-center justify-center text-xs font-700">
                {s.step}
              </div>
              <div className="hidden sm:block">
                <p className="text-xs font-600 text-foreground">{s.label}</p>
                <p className="text-[10px] text-muted-foreground">{s.desc}</p>
              </div>
            </div>
            {i < 2 && <Icon name="ChevronRightIcon" size={14} className="text-muted-foreground shrink-0" />}
          </React.Fragment>
        ))}
        {pendingPhysical > 0 && (
          <div className="ml-auto flex items-center gap-2 bg-accent/10 border border-accent/30 px-3 py-1.5 rounded-lg">
            <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
            <span className="text-xs font-600 text-accent-foreground">
              {pendingPhysical} GI{pendingPhysical !== 1 ? 's' : ''} awaiting physical issuance
            </span>
          </div>
        )}
      </div>

      {/* Status tabs */}
      <div className="flex gap-1 flex-wrap">
        {STATUS_TABS.map((tab) => (
          <button
            key={`gi-tab-${tab}`}
            onClick={() => setStatusTab(tab)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-500 transition-all border ${
              statusTab === tab ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-muted-foreground hover:text-foreground hover:border-muted-foreground/40'
            }`}
          >
            {tab === 'ALL' ? 'All GIs' : tab.replace('_', ' ')}
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-600 ${statusTab === tab ? 'bg-white/20' : 'bg-muted text-muted-foreground'}`}>
              {counts[tab]}
            </span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Icon name="MagnifyingGlassIcon" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search GI number, requester, BOM project…"
          className="w-full pl-9 pr-4 py-2.5 border border-border rounded-lg text-sm bg-card outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring transition-all"
        />
      </div>

      {/* Table */}
      <GITable gis={filtered} onViewDetail={setDetailGI} />

      {/* Wizard */}
      <GIWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />

      {/* Detail panel */}
      {detailGI && <GIDetailPanel gi={detailGI} onClose={() => setDetailGI(null)} />}
    </div>
  );
}